import DOMPurify from 'dompurify'
import validator from 'validator'

/**
 * Security utilities for TrainIQ Frontend
 * Protects against XSS, SQL injection, and other security vulnerabilities
 */

// XSS Protection
export const sanitizeHTML = (input: string): string => {
  if (!input) return ''
  return DOMPurify.sanitize(input, {
    ALLOWED_TAGS: [], // No HTML tags allowed
    ALLOWED_ATTR: []  // No attributes allowed
  })
}

// Safe text display (strips all HTML)
export const sanitizeText = (input: string): string => {
  if (!input) return ''
  // Remove all HTML tags and entities
  return DOMPurify.sanitize(input, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] })
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#x27;/g, "'")
}

// Input validation for forms
export const validateUserInput = {
  name: (name: string): { isValid: boolean; error?: string } => {
    if (!name || name.trim().length === 0) {
      return { isValid: false, error: 'Name ist erforderlich' }
    }
    if (name.length > 100) {
      return { isValid: false, error: 'Name darf nicht länger als 100 Zeichen sein' }
    }
    // Check for suspicious patterns
    if (/<script|javascript:|data:|vbscript:/i.test(name)) {
      return { isValid: false, error: 'Ungültiger Name' }
    }
    return { isValid: true }
  },

  email: (email: string): { isValid: boolean; error?: string } => {
    if (!email) {
      return { isValid: false, error: 'E-Mail ist erforderlich' }
    }
    if (!validator.isEmail(email)) {
      return { isValid: false, error: 'Ungültige E-Mail-Adresse' }
    }
    if (email.length > 254) {
      return { isValid: false, error: 'E-Mail ist zu lang' }
    }
    return { isValid: true }
  },

  age: (age: number): { isValid: boolean; error?: string } => {
    if (!age || age < 12 || age > 120) {
      return { isValid: false, error: 'Alter muss zwischen 12 und 120 Jahren liegen' }
    }
    return { isValid: true }
  },

  trainingContent: (content: string): { isValid: boolean; error?: string } => {
    if (!content || content.trim().length === 0) {
      return { isValid: false, error: 'Training-Inhalt ist erforderlich' }
    }
    if (content.length > 2000) {
      return { isValid: false, error: 'Training-Inhalt darf nicht länger als 2000 Zeichen sein' }
    }
    // Check for suspicious patterns
    if (/<script|javascript:|data:|vbscript:|on\w+\s*=/i.test(content)) {
      return { isValid: false, error: 'Ungültiger Inhalt erkannt' }
    }
    return { isValid: true }
  },

  notes: (notes: string): { isValid: boolean; error?: string } => {
    if (notes.length > 1000) {
      return { isValid: false, error: 'Notizen dürfen nicht länger als 1000 Zeichen sein' }
    }
    // Check for suspicious patterns
    if (/<script|javascript:|data:|vbscript:|on\w+\s*=/i.test(notes)) {
      return { isValid: false, error: 'Ungültiger Inhalt in Notizen erkannt' }
    }
    return { isValid: true }
  },

  duration: (duration: number): { isValid: boolean; error?: string } => {
    if (!duration || duration < 1 || duration > 480) { // Max 8 hours
      return { isValid: false, error: 'Dauer muss zwischen 1 und 480 Minuten liegen' }
    }
    return { isValid: true }
  }
}

// SQL Injection Protection (for direct queries - Supabase handles most)
export const sanitizeForQuery = (input: string): string => {
  if (!input) return ''
  // Basic SQL injection prevention
  return input
    .replace(/'/g, "''")  // Escape single quotes
    .replace(/;/g, '')    // Remove semicolons
    .replace(/--/g, '')   // Remove SQL comments
    .replace(/\/\*/g, '') // Remove block comments
    .replace(/\*\//g, '')
    .trim()
}

// Admin input validation
export const validateAdminInput = {
  notes: (notes: string): { isValid: boolean; error?: string } => {
    if (notes.length > 500) {
      return { isValid: false, error: 'Admin-Notizen dürfen nicht länger als 500 Zeichen sein' }
    }
    // Even admin input should be sanitized
    if (/<script|javascript:|data:|vbscript:/i.test(notes)) {
      return { isValid: false, error: 'Ungültiger Inhalt in Admin-Notizen' }
    }
    return { isValid: true }
  }
}

// Rate limiting helpers (client-side basic protection)
const requestCounts = new Map<string, { count: number; resetTime: number }>()

export const checkRateLimit = (key: string, maxRequests: number = 10, windowMs: number = 60000): boolean => {
  const now = Date.now()
  const record = requestCounts.get(key)

  if (!record || now > record.resetTime) {
    requestCounts.set(key, { count: 1, resetTime: now + windowMs })
    return true
  }

  if (record.count >= maxRequests) {
    return false
  }

  record.count++
  return true
}

// Content Security Policy helpers
export const getSecureImageSrc = (src: string): string => {
  // Only allow data URLs and HTTPS URLs from trusted domains
  if (src.startsWith('data:image/')) return src
  if (src.startsWith('https://')) return src
  return '' // Block anything else
}

// Debug: Security logging (remove in production)
export const logSecurityEvent = (event: string, data?: any) => {
  if (process.env.NODE_ENV === 'development') {
    console.warn(`🔒 SECURITY: ${event}`, data)
  }
}